import { Tooltip } from './Tooltip';

interface VersionBadgeProps {
    /** Raw version from Monday (`"V1"`, `"V2"`, or null/empty for unset). */
    value: string | null | undefined;
    className?: string;
}

/**
 * True when a raw patient-version value means the V2 flow.
 *
 * The stored value comes from Monday and is only uppercased at patient-creation
 * time, so rows written or migrated by other paths can carry arbitrary casing or
 * surrounding whitespace - normalize before comparing. Anything not recognisably
 * V2 counts as V1, per product decision.
 */
function isV2PatientVersion(value: string | null | undefined): boolean {
    return (value ?? '').trim().toUpperCase() === 'V2';
}

/**
 * Small pill showing whether a patient is on the V1 or V2 flow.
 * Falls back to V1 when the field is missing, per product decision.
 */
function VersionBadge({ value, className = '' }: VersionBadgeProps) {
    const isV2 = isV2PatientVersion(value);
    const label = isV2 ? 'v2' : 'v1';
    const styles = isV2
        ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/20'
        : 'bg-gray-50 text-gray-600 ring-gray-500/20';
    const dotColor = isV2 ? 'bg-emerald-500' : 'bg-gray-400';
    return (
        <Tooltip content={`Patient flow version: ${label.toUpperCase()}`}>
            <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium tracking-wide ring-1 ring-inset ${styles} ${className}`}
            >
                <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} aria-hidden="true" />
                {label}
            </span>
        </Tooltip>
    );
}

export { VersionBadge, isV2PatientVersion };
